import { useEffect, useState } from 'react';

import {
  type Student as ApiStudent,
  useGetStudentsInGroupBySession,
} from '~entities/student/api/get-students-in-group-by-session';

import { GroupSelector } from '~features/shared/group-selector';
import { SessionSelector } from '~features/shared/session-selector';

import type { Student } from '~pages/payments/bulk-charge-students';

import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';

interface SelectStudentByGroupTabProps {
  selectedStudents: Array<Student>;
  setSelectedStudents: React.Dispatch<React.SetStateAction<Array<Student>>>;
}

export const SelectStudentByGroupTab = ({
  selectedStudents,
  setSelectedStudents,
}: SelectStudentByGroupTabProps) => {
  const [selectedSession, setSelectedSession] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);

  const [selectedGroup, setSelectedGroup] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);

  // Only fetch students when both session and group are selected
  const shouldFetchStudents = selectedSession?.value && selectedGroup?.value;

  const {
    data: studentsResponse,
    isLoading: isLoadingStudents,
    error: studentsError,
  } = useGetStudentsInGroupBySession({
    sessionId: selectedSession?.value ?? '',
    groupId: selectedGroup?.value ?? '',
    showAll: true,
  });

  useEffect(() => {
    setSelectedStudents(
      studentsResponse?.data.map((student) => ({
        _id: student.id,
        name: student.name,
      })) ?? [],
    );
  }, [setSelectedStudents, studentsResponse?.data]);

  const handleGroupChange = (
    group:
      | {
          label: string;
          value: string;
        }
      | null
      | undefined,
  ) => {
    setSelectedGroup(group);
    setSelectedStudents([]);
  };

  const handleSelectStudent = (student: Student) => {
    const isSelected = selectedStudents.some((s) => s._id === student._id);
    if (isSelected) {
      setSelectedStudents(
        selectedStudents.filter((s) => s._id !== student._id),
      );
    } else {
      setSelectedStudents([...selectedStudents, student]);
    }
  };

  const handleRemoveStudent = (student: Student) => {
    setSelectedStudents(selectedStudents.filter((s) => s._id !== student._id));
  };

  return (
    <div className='space-y-4'>
      <div className='space-y-1.5'>
        <label
          htmlFor='session-selector'
          className='text-sm font-medium text-slate-700'
        >
          Session
        </label>
        <SessionSelector
          session={selectedSession}
          onChange={setSelectedSession}
        />
      </div>

      <div className='space-y-1.5'>
        <label
          htmlFor='group-selector'
          className='text-sm font-medium text-slate-700'
        >
          Group
        </label>
        <GroupSelector group={selectedGroup} onChange={handleGroupChange} />
      </div>

      {/* Students List Section */}
      {shouldFetchStudents && (
        <div className='space-y-3'>
          <div className='flex items-center justify-between px-1'>
            <h3 className='text-sm font-medium text-slate-700'>
              Students in Selected Group
            </h3>
            {selectedStudents.length > 0 && (
              <Button
                variant='outline'
                size='sm'
                className='h-7 px-2 text-xs'
                onClick={() => {
                  setSelectedStudents([]);
                }}
              >
                Clear all
              </Button>
            )}
          </div>

          <ScrollArea className='h-[300px] rounded-md border'>
            <div className='p-2'>
              {isLoadingStudents ? (
                <div className='flex h-32 items-center justify-center'>
                  <div className='text-sm text-slate-500'>
                    Loading students...
                  </div>
                </div>
              ) : studentsError ? (
                <div className='flex h-32 items-center justify-center'>
                  <div className='text-sm text-red-500'>
                    Error loading students: {studentsError.message}
                  </div>
                </div>
              ) : studentsResponse ? (
                studentsResponse.data.length > 0 ? (
                  <>
                    {studentsResponse.data.map((student: ApiStudent) => {
                      const studentForSelection: Student = {
                        _id: student.id,
                        name: student.name,
                        familyName:
                          student.name.split(' ').slice(1).join(' ') || '',
                      };

                      return (
                        <div
                          key={student.id}
                          className='flex cursor-pointer items-center space-x-3 rounded-md px-2 py-1.5 hover:bg-muted'
                          onClick={(e) => {
                            // Only handle click if it's not on the checkbox
                            if (
                              !(e.target as HTMLElement).closest(
                                '[role="checkbox"]',
                              )
                            ) {
                              handleSelectStudent(studentForSelection);
                            }
                          }}
                        >
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Checkbox
                              checked={selectedStudents.some(
                                (s) => s._id === studentForSelection._id,
                              )}
                              onCheckedChange={(checked) => {
                                if (checked !== 'indeterminate') {
                                  handleSelectStudent(studentForSelection);
                                }
                              }}
                              className='size-4'
                            />
                          </div>
                          <div className='flex flex-1 items-center justify-between'>
                            <p className='text-sm font-medium leading-none'>
                              {studentForSelection.name}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className='py-8 text-center text-sm text-slate-500'>
                    No students found in this group for the selected session.
                  </div>
                )
              ) : (
                <div className='py-8 text-center text-sm text-slate-500'>
                  No data available
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Selected Students Display */}
      {selectedStudents.length > 0 && (
        <div className='space-y-2'>
          <div className='flex items-center space-x-2'>
            <span className='text-sm font-medium'>
              {selectedStudents.length} students selected
            </span>
          </div>

          <ScrollArea className='h-[200px] rounded-md border bg-slate-50'>
            <div className='p-2'>
              {selectedStudents.map((student) => (
                <div
                  key={student._id}
                  className='group flex items-center space-x-3 rounded-md px-2 py-1.5 hover:bg-white'
                >
                  <div className='flex flex-1 items-center justify-between'>
                    <p className='text-sm font-medium leading-none'>
                      {student.name}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStudent(student);
                      }}
                      className='flex size-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                      aria-label={`Remove ${student.name}`}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
